import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon, Visibility as VisibilityIcon, Download as DownloadIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel, GridFilterModel } from '@mui/x-data-grid';
import apiService from '../services/apiService';
import ContactDetailModal from './ContactDetailModal';
import { format, parseISO } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import { obfuscateEmail } from '../utils/roleUtils';

interface ContactListModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  dataSource: 'email_performance' | 'engagement' | 'custom' | 'segment';
  initialIncludeSpamChecker?: boolean;
  config: {
    // For email_performance data source
    emailId?: number;
    emailName?: string;
    metric?: 'sent' | 'opens' | 'clicks' | 'unique_clicks';
    clientId: number;
    dateRange: {
      date_range: string;
      startDate?: Date;
      endDate?: Date;
    };
    // For engagement data source
    engagementMetric?: 'sent' | 'opens' | 'clicks';
    engagementSortBy?: 'top' | 'recent';
    // For custom data source
    customEndpoint?: string;
    customParams?: Record<string, any>;
    // For segment data source
    segmentId?: number;
    segmentName?: string;
  } | null;
}

interface ContactData {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  activity_date: string;
  last_activity: string;
  open_count?: number;
  click_count?: number;
  latitude?: number;
  longitude?: number;
}

interface ApiResponse {
  contacts: ContactData[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
  email: {
    id: number;
    name: string;
    subject: string;
    scheduled_date: string;
  };
  metric: string;
}

const ContactListModal: React.FC<ContactListModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  dataSource,
  initialIncludeSpamChecker = false,
  config
}) => {
  const { isICRole } = useUser();
  const [contacts, setContacts] = useState<ContactData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  const [includeSpamChecker, setIncludeSpamChecker] = useState(initialIncludeSpamChecker);

  // Update spam checker state when initial value changes
  useEffect(() => {
    setIncludeSpamChecker(initialIncludeSpamChecker);
  }, [initialIncludeSpamChecker]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  const getDefaultSortField = () => {
    if (!config) return 'activity_date';
    
    if (dataSource === 'email_performance' && config?.metric) {
      return config.metric === 'sent' ? 'email_address' : 
             config.metric === 'opens' ? 'open_count' : 
             config.metric === 'clicks' || config.metric === 'unique_clicks' ? 'click_count' : 
             'activity_date';
    } else if (dataSource === 'engagement' && config?.engagementMetric) {
      return config.engagementMetric === 'sent' ? 'sends' : 
             config.engagementMetric === 'opens' ? 'opens' : 'clicks';
    } else if (dataSource === 'segment') {
      return 'email_address'; // Default to email address for segments
    }
    return 'activity_date';
  };

  const getDefaultSortOrder = () => {
    if (!config) return 'desc';
    
    if (dataSource === 'email_performance' && config?.metric) {
      return config.metric === 'sent' ? 'asc' : 'desc'; // Email addresses should be alphabetical (asc)
    } else if (dataSource === 'engagement') {
      return 'desc'; // Counts should be descending
    } else if (dataSource === 'segment') {
      return 'asc'; // Email addresses should be alphabetical for segments
    }
    return 'desc';
  };

  // DataGrid state
  // Load saved page size from localStorage, default to 25
  const getSavedPageSize = () => {
    try {
      const saved = localStorage.getItem('contactListModal_pageSize');
      return saved ? parseInt(saved, 10) : 25;
    } catch {
      return 25;
    }
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: getSavedPageSize(),
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Initialize sort model after component mounts
  useEffect(() => {
    if (sortModel.length === 0 && config) {
      setSortModel([{ field: getDefaultSortField(), sort: getDefaultSortOrder() }]);
    }
  }, [dataSource, config?.metric, config?.engagementMetric]);

  // Helper function to convert filter model to API parameters
  const getFilterParams = () => {
    const filters: any = {};
    filterModel.items.forEach(item => {
      if (item.value) {
        switch (item.field) {
          case 'email_address':
            filters.email_address = item.value;
            break;
          case 'first_name':
            filters.first_name = item.value;
            break;
          case 'last_name':
            filters.last_name = item.value;
            break;
        }
      }
    });
    return filters;
  };

  // Debounced API call to prevent excessive requests
  const debouncedFetch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (params: any) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchContacts(params);
        }, 300); // 300ms delay
      };
    })(),
    [includeSpamChecker, paginationModel, sortModel, filterModel]
  );

  // Export contacts to CSV
  const handleExport = useCallback(async () => {
    if (!config.clientId || isExporting) return;

    setIsExporting(true);
    setExportProgress({ current: 0, total: 0 });
    try {
      // Fetch all contacts for export using pagination
      let allContacts: any[] = [];
      let clientName = 'unknown';
      let totalCount = 0;

      // Fetch client name
      try {
        const clientResponse = await apiService.get(`/api/clients/${config.clientId}`);
        if (clientResponse.data?.agency_name) {
          clientName = clientResponse.data.agency_name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        }
      } catch (error) {
        console.warn('Could not fetch client name, using client ID:', error);
        clientName = `client_${config.clientId}`;
      }

      // First, get total count to show progress
      const initialParams = {
        page: 1,
        limit: 1000,
        includeSpamChecker: includeSpamChecker,
        ...getFilterParams()
      };

      let initialResponse;
      if (dataSource === 'email_performance' && config.emailId) {
        initialResponse = await apiService.get(`/api/analytics/email-performance/${config.emailId}/contacts`, {
          params: {
            client_id: config.clientId,
            metric: config.metric,
            date_range: config.dateRange.date_range,
            start_date: config.dateRange.startDate?.toISOString(),
            end_date: config.dateRange.endDate?.toISOString(),
            include_spam: !initialParams.includeSpamChecker,
            ...initialParams
          }
        });
      } else if (dataSource === 'engagement') {
        // Use separate endpoints based on engagement metric
        const endpoint = `/api/analytics/engagement/${config.engagementMetric}`;
        initialResponse = await apiService.get(endpoint, {
          params: {
            client_id: config.clientId,
            sort_by: config.engagementSortBy || 'top',
            date_range: config.dateRange.date_range,
            start_date: config.dateRange.startDate?.toISOString(),
            end_date: config.dateRange.endDate?.toISOString(),
            include_spam: !initialParams.includeSpamChecker,
            ...initialParams
          }
        });
      }

      if (initialResponse?.data?.contacts) {
        allContacts = initialResponse.data.contacts;
        totalCount = initialResponse.data.total_count || 0;
        setExportProgress({ current: allContacts.length, total: totalCount });
      }

      // Continue fetching remaining pages if needed
      const limit = 1000;
      let page = 2;
      let hasMore = allContacts.length < totalCount;

      while (hasMore) {
        const exportParams = {
          page,
          limit,
          includeSpamChecker: includeSpamChecker,
          ...getFilterParams()
        };

        let response;
        if (dataSource === 'email_performance' && config.emailId) {
          response = await apiService.get(`/api/analytics/email-performance/${config.emailId}/contacts`, {
            params: {
              client_id: config.clientId,
              metric: config.metric,
              date_range: config.dateRange.date_range,
              start_date: config.dateRange.startDate?.toISOString(),
              end_date: config.dateRange.endDate?.toISOString(),
              include_spam: !exportParams.includeSpamChecker,
              ...exportParams
            }
          });
        } else if (dataSource === 'engagement') {
          // Use separate endpoints based on engagement metric
          const endpoint = `/api/analytics/engagement/${config.engagementMetric}`;
          response = await apiService.get(endpoint, {
            params: {
              client_id: config.clientId,
              sort_by: config.engagementSortBy || 'top',
              date_range: config.dateRange.date_range,
              start_date: config.dateRange.startDate?.toISOString(),
              end_date: config.dateRange.endDate?.toISOString(),
              include_spam: !exportParams.includeSpamChecker,
              ...exportParams
            }
          });
        }

        if (response?.data?.contacts) {
          allContacts = allContacts.concat(response.data.contacts);
          
          // Update progress
          setExportProgress({ current: allContacts.length, total: totalCount });
          
          // Check if we've fetched all records
          if (allContacts.length >= totalCount || response.data.contacts.length < limit) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }

      if (allContacts.length === 0) {
        alert('No contacts to export');
        return;
      }

      // Generate filename based on client name, metric type, and date
      const metricType = dataSource === 'email_performance' ? 
        (config.metric || 'sent') : 
        dataSource === 'engagement' ? (config.engagementMetric || 'opens') :
        dataSource === 'segment' ? (config.segmentName || 'segment') :
        'contacts';
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const filename = `${clientName}_${metricType}_contacts_${dateStr}.csv`;

      // Create CSV headers based on available columns
      const headers = ['First Name', 'Last Name', 'Email Address'];
      
      // Add data source specific columns
      if (dataSource === 'email_performance') {
        headers.push('Activity Date');
        if (config.metric === 'sent') {
          headers.push('Open Count', 'Click Count');
        } else if (config.metric === 'opens') {
          headers.push('Open Count', 'Click Count');
        } else if (config.metric === 'clicks' || config.metric === 'unique_clicks') {
          headers.push('Open Count', 'Click Count');
        }
      } else if (dataSource === 'engagement') {
        headers.push('Activity Date', 'Sends', 'Opens', 'Clicks');
      } else if (dataSource === 'segment') {
        headers.push('Mobile Phone', 'City', 'State', 'Country', 'Created Date');
      }

      // Convert contacts to CSV rows
      const csvRows = [
        headers.join(','),
        ...allContacts.map((contact: any) => {
          const row = [
            `"${contact.first_name || ''}"`,
            `"${contact.last_name || ''}"`,
            `"${isICRole() ? obfuscateEmail(contact.email_address) : (contact.email_address || '')}"`
          ];

          // Add data source specific data
          if (dataSource === 'email_performance') {
            const activityDate = contact.last_activity || contact.activity_date;
            row.push(`"${activityDate ? format(parseISO(activityDate), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}"`);
            
            if (config.metric === 'sent') {
              row.push(`"${contact.open_count || 0}"`, `"${contact.click_count || 0}"`);
            } else if (config.metric === 'opens') {
              row.push(`"${contact.open_count || 0}"`, `"${contact.click_count || 0}"`);
            } else if (config.metric === 'clicks' || config.metric === 'unique_clicks') {
              row.push(`"${contact.open_count || 0}"`, `"${contact.click_count || 0}"`);
            }
          } else if (dataSource === 'engagement') {
            const activityDate = contact.last_activity || contact.activity_date;
            row.push(`"${activityDate ? format(parseISO(activityDate), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}"`);
            row.push(`"${contact.sends || 0}"`, `"${contact.opens || 0}"`, `"${contact.clicks || 0}"`);
          } else if (dataSource === 'segment') {
            row.push(
              `"${contact.mobile_phone || ''}"`,
              `"${contact.city || ''}"`,
              `"${contact.state || ''}"`,
              `"${contact.country || ''}"`,
              `"${contact.created_at ? format(parseISO(contact.created_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}"`
            );
          }

          return row.join(',');
        })
      ];

      // Create and download CSV file
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export contacts. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [config, dataSource, includeSpamChecker, getFilterParams, isExporting]);

  // Clear data when modal opens
  useEffect(() => {
    if (open) {
      // Clear previous data to prevent showing stale data
      setContacts([]);
      setTotalCount(0);
      setError(null);
      setLoading(false);
      
      // Clear backend cache to ensure fresh data
      if (dataSource === 'engagement') {
        apiService.post('/api/analytics/engagement/clear-cache')
          .then(() => {
            console.log('Backend cache cleared for fresh data');
          })
          .catch((error) => {
            console.warn('Failed to clear backend cache:', error);
            // Don't show error to user, just log it
          });
      }
    }
  }, [open, dataSource]);

  // Clear data immediately when config changes (to prevent FOUC)
  useEffect(() => {
    if (open) {
      // Clear data immediately when config changes to prevent showing wrong data
      setContacts([]);
      setTotalCount(0);
      setError(null);
      setLoading(true); // Show loading while fetching new data
    }
  }, [config, open]);

  // Fetch data when modal opens or when spam filter changes
  useEffect(() => {
    if (open && config && config.clientId) {
      // Check if we have the required fields for the dataSource
      const hasRequiredFields = 
        (dataSource === 'email_performance' && config.emailId && config.metric) ||
        (dataSource === 'engagement' && config.engagementMetric) ||
        (dataSource === 'custom' && config.customEndpoint) ||
        (dataSource === 'segment' && config.segmentId);
      
      if (hasRequiredFields) {
        const params = {
          includeSpamChecker
        };
        
        debouncedFetch(params);
      }
    }
  }, [open, dataSource, config, includeSpamChecker, debouncedFetch, sortModel, filterModel, paginationModel]);

  const fetchContacts = async (params: any) => {
    if (!config || !config.clientId) {
      return;
    }
    
    // Check if we have the required fields for the dataSource
    const hasRequiredFields = 
      (dataSource === 'email_performance' && config.emailId && config.metric) ||
      (dataSource === 'engagement' && config.engagementMetric) ||
      (dataSource === 'custom' && config.customEndpoint) ||
      (dataSource === 'segment' && config.segmentId);
    
    if (!hasRequiredFields) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      let endpoint = '';
      let apiParams: any = {
        client_id: config.clientId,
        include_spam: !params.includeSpamChecker,
        page: paginationModel.page + 1, // Convert 0-based to 1-based for API
        limit: paginationModel.pageSize, // Pass the selected page size
        sort_by_field: sortModel.length > 0 ? sortModel[0].field : undefined,
        sort_order: sortModel.length > 0 ? sortModel[0].sort : undefined,
        include_geographic: true, // Always include geographic data for map compatibility
        ...getFilterParams()
      };

      // Configure API call based on data source
      switch (dataSource) {
        case 'email_performance':
          if (!config.emailId || !config.metric) {
            return;
          }
          endpoint = `/api/analytics/email-performance/${config.emailId}/contacts`;
          apiParams = {
            ...apiParams,
            metric: config.metric,
            date_range: config.dateRange.date_range,
            start_date: config.dateRange.startDate?.toISOString(),
            end_date: config.dateRange.endDate?.toISOString(),
          };
          break;
        case 'engagement':
          // Use separate endpoints based on engagement metric
          if (config.engagementMetric === 'sent') {
            // Use the new all-sent endpoint for sent metric
            endpoint = `/api/analytics/engagement/all-sent`;
          } else {
            // Use regular engagement endpoints for opens/clicks
            endpoint = `/api/analytics/engagement/${config.engagementMetric}`;
          }
          // Spam filter only applies to clicks, not to sent or opens
          const useSpamFilter = config.engagementMetric === 'clicks' ? !params.includeSpamChecker : true;
          apiParams = {
            ...apiParams,
            include_spam: useSpamFilter,
            sort_by: config.engagementSortBy || 'top',
            date_range: config.dateRange.date_range,
            start_date: config.dateRange.startDate?.toISOString(),
            end_date: config.dateRange.endDate?.toISOString(),
          };
          break;
        case 'custom':
          if (!config.customEndpoint) return;
          endpoint = config.customEndpoint;
          apiParams = { ...apiParams, ...config.customParams };
          break;
        case 'segment':
          if (!config.segmentId) return;
          endpoint = `/api/segments/${config.segmentId}/contacts`;
          // Segment contacts don't need date range or spam filtering
          apiParams = {
            client_id: config.clientId,
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            sort_by_field: sortModel.length > 0 ? sortModel[0].field : undefined,
            sort_order: sortModel.length > 0 ? sortModel[0].sort : undefined,
            ...getFilterParams()
          };
          break;
      }

      // Use longer timeout for all-sent endpoint due to large dataset
      const timeout = endpoint.includes('/all-sent') ? 60000 : 30000;
      
      // Debug logging
      console.log('ContactListModal API call:', {
        endpoint,
        client_id: apiParams.client_id,
        config_clientId: config?.clientId,
        apiParams
      });
      
      const response = await apiService.get(endpoint, { 
        params: apiParams,
        timeout: timeout
      });
      
      const data = response.data as ApiResponse;
      setContacts(data.contacts || []);
      setTotalCount(data.total_count || 0);
      
      // Only log export progress during actual export operations
      if (isExporting) {
        const totalPages = Math.ceil((data.total_count || 0) / 5000); // Backend uses 5000 per page
        if (totalPages > 1) {
          console.log(`Export progress: Page ${paginationModel.page + 1} of ${totalPages} (${data.contacts?.length || 0} contacts loaded)`);
        }
      }
    } catch (err: any) {
      console.error('Contact list API error:', err);
      setError(`Failed to load contacts: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    
    // Save page size to localStorage
    try {
      localStorage.setItem('contactListModal_pageSize', newModel.pageSize.toString());
    } catch (error) {
      console.warn('Could not save page size to localStorage:', error);
    }
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
  };

  const handleFilterModelChange = (newModel: GridFilterModel) => {
    setFilterModel(newModel);
  };

  const handleContactClick = (contact: ContactData) => {
    setSelectedContact(contact);
  };

  const handleCloseContactModal = () => {
    setSelectedContact(null);
  };


  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      field: 'email_address',
      headerName: 'Email Address',
      width: 250,
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => handleContactClick(params.row)}
          sx={{
            textAlign: 'left',
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 'normal'
          }}
        >
          {isICRole() ? obfuscateEmail(params.value) : params.value}
        </Button>
      ),
    },
    {
      field: dataSource === 'engagement' ? 'last_activity' : 'activity_date',
      headerName: 'Activity Date',
      width: 150,
      sortable: true,
      valueFormatter: (params) => {
        // Handle both params.value and direct params (DataGrid API inconsistency)
        const value = params?.value || params;
        if (!value) return 'N/A';
        try {
          return format(parseISO(value), 'yyyy-MM-dd');
        } catch (error) {
          console.error('Date parsing error:', error, 'for value:', value);
          return 'Invalid Date';
        }
      },
    },
    ...(dataSource === 'email_performance' && config && (config.metric === 'sent' || config.metric === 'opens' || config.metric === 'clicks' || config.metric === 'unique_clicks') ? [
      {
        field: 'open_count',
        headerName: 'Opens',
        width: 100,
        sortable: true,
        type: 'number' as const,
      },
      {
        field: 'click_count',
        headerName: 'Clicks',
        width: 120,
        sortable: true,
        type: 'number' as const,
      }
    ] : []),
    ...(dataSource === 'engagement' ? [
      {
        field: 'sends',
        headerName: 'Sends',
        width: 100,
        sortable: true,
        type: 'number' as const,
      },
      {
        field: 'opens',
        headerName: 'Opens',
        width: 100,
        sortable: true,
        type: 'number' as const,
      },
      {
        field: 'clicks',
        headerName: 'Clicks',
        width: 100,
        sortable: true,
        type: 'number' as const,
      }
    ] : []),
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {!isICRole() && (
                isExporting ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Exporting... ({exportProgress.current.toLocaleString()}/{exportProgress.total.toLocaleString()})
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip title="Export contacts to CSV">
                    <IconButton
                      onClick={handleExport}
                      disabled={contacts.length === 0}
                      size="small"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <Paper elevation={1} sx={{ height: '60vh', width: '100%' }}>
              <DataGrid
                rows={contacts}
                columns={columns}
                rowCount={totalCount}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                paginationModel={paginationModel}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                sortModel={sortModel}
                filterMode="server"
                onFilterModelChange={handleFilterModelChange}
                filterModel={filterModel}
                pageSizeOptions={[25, 50, 100, 250]}
                disableRowSelectionOnClick
                loading={loading}
                getRowId={(row) => row.id}
                autoHeight={false}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                  },
                }}
              />
            </Paper>
          )}

          {contacts.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No contacts found.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {selectedContact && (
        <ContactDetailModal
          open={!!selectedContact}
          onClose={handleCloseContactModal}
          contactId={selectedContact.id}
          clientId={config.clientId}
          dateRange={config.dateRange}
        />
      )}
    </>
  );
};

export default ContactListModal;
